const EAN13_LEFT_ODD = {
  '0': '0001101',
  '1': '0011001',
  '2': '0010011',
  '3': '0111101',
  '4': '0100011',
  '5': '0110001',
  '6': '0101111',
  '7': '0111011',
  '8': '0110111',
  '9': '0001011'
}

const EAN13_LEFT_EVEN = {
  '0': '0100111',
  '1': '0110011',
  '2': '0011011',
  '3': '0100001',
  '4': '0011101',
  '5': '0111001',
  '6': '0000101',
  '7': '0010001',
  '8': '0001001',
  '9': '0010111'
}

const EAN13_RIGHT = {
  '0': '1110010',
  '1': '1100110',
  '2': '1101100',
  '3': '1000010',
  '4': '1011100',
  '5': '1001110',
  '6': '1010000',
  '7': '1000100',
  '8': '1001000',
  '9': '1110100'
}

const EAN13_PARITY = {
  '0': 'OOOOOO',
  '1': 'OOEOEE',
  '2': 'OOEEOE',
  '3': 'OOEEEO',
  '4': 'OEOOEE',
  '5': 'OEEOOE',
  '6': 'OEEEOO',
  '7': 'OEOEOE',
  '8': 'OEOEEO',
  '9': 'OEEOEO'
}

const escapeXml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

const calculateEan13CheckDigit = (digits12) => {
  const numbers = digits12.split('').map(Number)
  const total = numbers.reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 1 : 3), 0)
  return String((10 - (total % 10)) % 10)
}

const resolveEan13Barcode = (barcode) => {
  const digits = String(barcode || '').replace(/\D/g, '')
  if (digits.length === 12) {
    return digits + calculateEan13CheckDigit(digits)
  }
  return digits.length === 13 ? digits : ''
}

const buildEan13Pattern = (barcode) => {
  const digits = resolveEan13Barcode(barcode)
  if (!digits) return null

  const first = digits[0]
  const leftDigits = digits.slice(1, 7).split('')
  const rightDigits = digits.slice(7).split('')
  const parity = EAN13_PARITY[first]

  if (!parity) return null

  const leftPattern = leftDigits.map((digit, index) => (
    parity[index] === 'E' ? EAN13_LEFT_EVEN[digit] : EAN13_LEFT_ODD[digit]
  )).join('')

  const rightPattern = rightDigits.map((digit) => EAN13_RIGHT[digit]).join('')

  return {
    digits,
    pattern: `101${leftPattern}01010${rightPattern}101`
  }
}

const createBarcodeSvg = ({ heading, title, barcode }) => {
  const width = 840
  const height = 400
  const leftPadding = 20
  const topPadding = 20
  const safeHeading = escapeXml(heading)
  const safeTitle = escapeXml(title)
  const safeBarcode = escapeXml(barcode)
  const bars = buildEan13Pattern(barcode)

  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    '<rect width="100%" height="100%" fill="#ffffff"/>',
    `<text x="${leftPadding}" y="${topPadding + 16}" font-family="Arial, Helvetica, sans-serif" font-size="30" font-weight="700" fill="#111827">${safeHeading}</text>`,
    `<text x="${leftPadding}" y="${topPadding + 48}" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="600" fill="#374151">${safeTitle}</text>`
  ]

  if (bars) {
    const patternLength = bars.pattern.length
    const barcodeWidth = 380
    const barWidth = barcodeWidth / patternLength
    const startX = leftPadding + 28
    const baseY = 112
    const barHeight = 108
    const guardHeight = 120
    const digitY = 280
    const firstDigitX = startX - 32
    const leftDigitsX = startX + (3 * barWidth) + 4
    const rightDigitsX = startX + (50 * barWidth) + 4
    const arrowStartX = startX + barcodeWidth + 8
    const arrowCenterY = digitY - 10
    const guardIndexes = new Set([0, 2, 45, 47, 92, 94])

    bars.pattern.split('').forEach((bit, index) => {
      if (bit !== '1') return
      const currentX = startX + index * barWidth
      const currentHeight = guardIndexes.has(index) ? guardHeight : barHeight
      parts.push(`<rect x="${currentX.toFixed(2)}" y="${baseY}" width="${barWidth.toFixed(2)}" height="${currentHeight}" fill="#000000"/>`)
    })

    parts.push(`<text x="${firstDigitX}" y="${digitY}" font-family="Arial, Helvetica, sans-serif" font-size="30" fill="#000000">${bars.digits[0]}</text>`)
    parts.push(`<text x="${leftDigitsX.toFixed(2)}" y="${digitY}" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#000000" letter-spacing="2">${bars.digits.slice(1, 7)}</text>`)
    parts.push(`<text x="${rightDigitsX.toFixed(2)}" y="${digitY}" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#000000" letter-spacing="2">${bars.digits.slice(7)}</text>`)
    parts.push(`<path d="M ${arrowStartX.toFixed(2)} ${arrowCenterY - 7} L ${(arrowStartX + 14).toFixed(2)} ${arrowCenterY} L ${arrowStartX.toFixed(2)} ${arrowCenterY + 7}" fill="none" stroke="#000000" stroke-width="2.4"/>`)
  } else {
    parts.push(`<text x="${leftPadding}" y="196" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#B45309">Неподдерживаемый формат штрих-кода</text>`)
    parts.push(`<text x="${leftPadding}" y="244" font-family="Arial, Helvetica, sans-serif" font-size="44" fill="#111827">${safeBarcode || 'Нет штрих-кода'}</text>`)
  }

  parts.push(`<text x="${leftPadding}" y="360" font-family="Arial, Helvetica, sans-serif" font-size="24" font-weight="600" fill="#4B5563">Код: ${safeBarcode || 'Не назначен'}</text>`)
  parts.push('</svg>')

  return parts.join('')
}

const svgToJpegDataUrl = async (svgMarkup, options = {}) => {
  const renderScale = Number(options.renderScale) > 0 ? Number(options.renderScale) : 2
  const quality = Number.isFinite(options.quality) ? options.quality : 1
  const blob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = url
    })

    const canvas = document.createElement('canvas')
    canvas.width = image.width * renderScale
    canvas.height = image.height * renderScale
    const context = canvas.getContext('2d')
    context.imageSmoothingEnabled = true
    context.imageSmoothingQuality = 'high'
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, canvas.width, canvas.height)
    context.drawImage(image, 0, 0, canvas.width, canvas.height)

    return {
      dataUrl: canvas.toDataURL('image/jpeg', quality),
      width: canvas.width,
      height: canvas.height
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}

const dataUrlToBinary = (dataUrl) => {
  const base64 = dataUrl.split(',')[1] || ''
  return atob(base64)
}

const binaryToHex = (binary) => {
  let hex = ''

  for (let index = 0; index < binary.length; index += 1) {
    hex += binary.charCodeAt(index).toString(16).padStart(2, '0').toUpperCase()
  }

  return `${hex}>`
}

const createPdfFromJpegBinary = ({ jpegBinary, imageWidth, imageHeight }) => {
  const pageWidth = 595.28
  const pageHeight = 841.89
  const renderWidth = 260
  const renderHeight = renderWidth * (imageHeight / imageWidth)
  const offsetX = (pageWidth - renderWidth) / 2
  const offsetY = pageHeight - 56 - renderHeight
  const contentStream = `q\n${renderWidth.toFixed(2)} 0 0 ${renderHeight.toFixed(2)} ${offsetX.toFixed(2)} ${offsetY.toFixed(2)} cm\n/Im0 Do\nQ\n`
  const jpegHex = binaryToHex(jpegBinary)
  const objects = []

  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj')
  objects.push('2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj')
  objects.push(`3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>
endobj`)
  objects.push(`4 0 obj
<< /Type /XObject /Subtype /Image /Width ${imageWidth} /Height ${imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${jpegHex.length} >>
stream
${jpegHex}
endstream
endobj`)
  objects.push(`5 0 obj
<< /Length ${contentStream.length} >>
stream
${contentStream}endstream
endobj`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  objects.forEach((object) => {
    offsets.push(pdf.length)
    pdf += `${object}\n`
  })

  const xrefStart = pdf.length
  pdf += `xref
0 ${objects.length + 1}
0000000000 65535 f 
`

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`
  })

  pdf += `trailer
<< /Size ${objects.length + 1} /Root 1 0 R >>
startxref
${xrefStart}
%%EOF`

  return pdf
}

const createPdfFromJpegPages = (pages) => {
  const pageWidth = 595.28
  const pageHeight = 841.89
  const objects = []
  const pageObjectIds = []

  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj')

  const kidsOffset = 3
  pages.forEach((page, index) => {
    const pageObjectId = kidsOffset + index * 3
    const imageObjectId = pageObjectId + 1
    const contentObjectId = pageObjectId + 2
    const jpegHex = binaryToHex(page.jpegBinary)
    const contentStream = `q\n${pageWidth.toFixed(2)} 0 0 ${pageHeight.toFixed(2)} 0 0 cm\n/Im0 Do\nQ\n`

    pageObjectIds.push(pageObjectId)
    objects.push(`${pageObjectId} 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im0 ${imageObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>
endobj`)
    objects.push(`${imageObjectId} 0 obj
<< /Type /XObject /Subtype /Image /Width ${page.imageWidth} /Height ${page.imageHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter [/ASCIIHexDecode /DCTDecode] /Length ${jpegHex.length} >>
stream
${jpegHex}
endstream
endobj`)
    objects.push(`${contentObjectId} 0 obj
<< /Length ${contentStream.length} >>
stream
${contentStream}endstream
endobj`)
  })

  objects.splice(1, 0, `2 0 obj\n<< /Type /Pages /Count ${pages.length} /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] >>\nendobj`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  objects.forEach((object) => {
    offsets.push(pdf.length)
    pdf += `${object}\n`
  })

  const xrefStart = pdf.length
  pdf += `xref
0 ${objects.length + 1}
0000000000 65535 f 
`

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`
  })

  pdf += `trailer
<< /Size ${objects.length + 1} /Root 1 0 R >>
startxref
${xrefStart}
%%EOF`

  return pdf
}

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

const createBatchPageSvg = (items) => {
  const width = 992
  const height = 1403
  const columns = 2
  const rows = 5
  const pagePaddingX = 56
  const pagePaddingY = 48
  const columnGap = 34
  const rowGap = 28
  const cardWidth = (width - pagePaddingX * 2 - columnGap) / columns
  const cardHeight = (height - pagePaddingY * 2 - rowGap * (rows - 1)) / rows
  const parts = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    '<rect width="100%" height="100%" fill="#ffffff"/>'
  ]

  items.forEach((item, index) => {
    const row = Math.floor(index / columns)
    const column = index % columns
    const x = pagePaddingX + column * (cardWidth + columnGap)
    const y = pagePaddingY + row * (cardHeight + rowGap)
    const bars = buildEan13Pattern(item.barcode)
    const cardPaddingX = 42
    const cardPaddingTop = 28
    const barcodeWidth = Math.min(cardWidth - cardPaddingX * 2, 420)
    const patternLength = bars?.pattern?.length || 0
    const barWidth = patternLength ? barcodeWidth / patternLength : 0
    const barcodeX = x + (cardWidth - barcodeWidth) / 2
    const barcodeY = y + 58
    const barHeight = 112
    const guardHeight = 126
    const digitsY = y + cardHeight - 26
    const guardIndexes = new Set([0, 2, 45, 47, 92, 94])

    parts.push(`<rect x="${x}" y="${y}" width="${cardWidth}" height="${cardHeight}" rx="30" fill="#ffffff" stroke="#bfd0ee" stroke-width="3"/>`)
    parts.push(`<text x="${(x + cardWidth / 2).toFixed(2)}" y="${(y + cardPaddingTop).toFixed(2)}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="26" font-weight="700" fill="#111827">${escapeXml(item.display_id || `#${item.entity_id || ''}`)}</text>`)

    if (bars) {
      bars.pattern.split('').forEach((bit, bitIndex) => {
        if (bit !== '1') return
        const currentX = barcodeX + bitIndex * barWidth
        const currentHeight = guardIndexes.has(bitIndex) ? guardHeight : barHeight
        parts.push(`<rect x="${currentX.toFixed(2)}" y="${barcodeY.toFixed(2)}" width="${Math.max(barWidth, 1.4).toFixed(2)}" height="${currentHeight}" fill="#000000"/>`)
      })

      const firstDigitX = barcodeX - 26
      const leftDigitsX = barcodeX + (3 * barWidth) + 2
      const rightDigitsX = barcodeX + (50 * barWidth) + 2
      parts.push(`<text x="${firstDigitX.toFixed(2)}" y="${digitsY.toFixed(2)}" font-family="Arial, Helvetica, sans-serif" font-size="30" fill="#000000">${bars.digits[0]}</text>`)
      parts.push(`<text x="${leftDigitsX.toFixed(2)}" y="${digitsY.toFixed(2)}" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#000000" letter-spacing="2">${bars.digits.slice(1, 7)}</text>`)
      parts.push(`<text x="${rightDigitsX.toFixed(2)}" y="${digitsY.toFixed(2)}" font-family="Arial, Helvetica, sans-serif" font-size="24" fill="#000000" letter-spacing="2">${bars.digits.slice(7)}</text>`)
    } else {
      parts.push(`<text x="${(x + cardWidth / 2).toFixed(2)}" y="${(y + cardHeight / 2).toFixed(2)}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="28" fill="#B45309">${escapeXml(item.barcode || 'Нет штрихкода')}</text>`)
    }
  })

  parts.push('</svg>')
  return parts.join('')
}

export const createBarcodeBatchPdfBlob = async ({ items, perPage = 10 }) => {
  const safeItems = Array.isArray(items) ? items.filter((item) => item?.barcode) : []
  if (!safeItems.length) {
    throw new Error('Нет штрихкодов для выгрузки')
  }

  const pageImages = []

  for (let index = 0; index < safeItems.length; index += perPage) {
    const pageItems = safeItems.slice(index, index + perPage)
    const svgMarkup = createBatchPageSvg(pageItems)
    const { dataUrl, width, height } = await svgToJpegDataUrl(svgMarkup, {
      renderScale: 1,
      quality: 0.82
    })
    pageImages.push({
      jpegBinary: dataUrlToBinary(dataUrl),
      imageWidth: width,
      imageHeight: height
    })
  }

  const pdfBinary = createPdfFromJpegPages(pageImages)
  return new Blob([pdfBinary], { type: 'application/pdf' })
}

export const downloadPdfBlob = (blob, fileName) => {
  downloadBlob(blob, fileName)
}

export const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => {
    const result = typeof reader.result === 'string' ? reader.result : ''
    const base64 = result.includes(',') ? result.split(',')[1] : result
    resolve(base64)
  }
  reader.onerror = () => reject(reader.error || new Error('Не удалось подготовить PDF к сохранению'))
  reader.readAsDataURL(blob)
})

export const exportBarcodePdfLabel = async ({ heading, title, barcode, fileName }) => {
  const svgMarkup = createBarcodeSvg({ heading, title, barcode })
  const { dataUrl, width, height } = await svgToJpegDataUrl(svgMarkup)
  const jpegBinary = dataUrlToBinary(dataUrl)
  const pdfBinary = createPdfFromJpegBinary({
    jpegBinary,
    imageWidth: width,
    imageHeight: height
  })

  const blob = new Blob([pdfBinary], { type: 'application/pdf' })
  downloadBlob(blob, fileName)
}
